import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import axiosInstance from "@/lib/axios";
import { campaignEndpoint, campaignFormsEndpoint } from "@/config/endpoints";
import { invalidateCustomFieldCaches } from "@/lib/queryClient";

const PAGE_SIZE = 24;

function cleanQueryParams(params: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) =>
        v !== undefined &&
        v !== null &&
        v !== "" &&
        !(Array.isArray(v) && v.length === 0)
    )
  );
}

export type CampaignSource = {
  id: number;
  name: string;
  type: string | null;
};

export type CampaignForm = {
  id: number;
  name: string;
  slug: string;
  isActive: boolean;
};

export type Campaign = {
  id: number;
  name: string;
  isEditable: boolean;
  isActive: boolean;
  origin: "META" | "CUSTOM" | "SYSTEM";
  createdAt: string;
  updatedAt: string;
  sources: CampaignSource[];
  forms: CampaignForm[];
  leadFormsCount: number;
  leadsGenerated: number;
};

export type CampaignFormQuestion = {
  key: string;
  label: string;
  type: string;
  options: string[];
  /** True for name/email/phone/country — these map natively, no toggle. */
  isStandard?: boolean;
  /** Whether this question's switch is ON (custom field actively captured). */
  enabled?: boolean;
  /** The custom field backing an enabled question (null when off/never on). */
  customInputId?: number | null;
};

export type CampaignDetail = Campaign & {
  topSource: CampaignSource | null;
  /** Distinct leads in this campaign that re-enquired at least once. */
  reenquiredLeads: number;
  formBreakdown: Array<{
    id: number;
    name: string;
    slug: string;
    isActive: boolean;
    totalLeads: number;
    sources: Array<CampaignSource & { count: number }>;
    /** Snapshot of the Meta form's questions/fields; [] for custom forms. */
    questions: CampaignFormQuestion[];
  }>;
};

export function useCampaigns(params: Record<string, any> = {}) {
  const cleanParams = cleanQueryParams(params);
  const query = useQuery({
    queryKey: ["campaigns", cleanParams],
    queryFn: async () => {
      const res = await axiosInstance.get(campaignEndpoint, { params: cleanParams });
      return {
        campaigns: (res.data.campaigns ?? []) as Campaign[],
        meta: res.data.meta,
      };
    },
  });

  return {
    campaigns: query.data?.campaigns,
    totalCount: Number(query.data?.meta?.total) || query.data?.campaigns?.length || 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useInfiniteCampaigns(
  params: Record<string, any> = {},
  pageSize = PAGE_SIZE
) {
  const cleanParams = cleanQueryParams(params);

  const query = useInfiniteQuery({
    queryKey: ["campaigns", "infinite", cleanParams, pageSize],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await axiosInstance.get(campaignEndpoint, {
        params: {
          ...cleanParams,
          page: pageParam,
          limit: pageSize,
        },
      });
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages) => {
      const total = Number(lastPage?.meta?.total) || 0;
      const loaded = allPages.flatMap((page: any) => page.campaigns ?? []).length;
      if (loaded > 0 && loaded < total) return allPages.length + 1;
      const lastBatch = (lastPage?.campaigns ?? []).length;
      if (!total && lastBatch === pageSize) return allPages.length + 1;
      return undefined;
    },
    select: (data) => {
      const campaigns = data.pages.flatMap((page: any) => page.campaigns ?? []);
      return {
        campaigns: campaigns as Campaign[],
        totalCount: Number(data.pages[0]?.meta?.total) || campaigns.length,
      };
    },
  });

  return {
    campaigns: query.data?.campaigns ?? [],
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}

export function useCampaign(id?: number | null) {
  const query = useQuery({
    queryKey: ["campaign", id],
    queryFn: async () => {
      const res = await axiosInstance.get(`${campaignEndpoint}/${id}`);
      return res.data.campaign as CampaignDetail;
    },
    enabled: !!id,
  });

  return {
    campaign: query.data,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

/**
 * Toggle a Meta form question's "capture as custom field" switch. ON creates
 * (or re-enables) a custom field that captures future leads' answers; OFF keeps
 * the field + collected answers but stops new capture. Optimistically flips the
 * switch in the cached campaign detail and invalidates custom-field caches so
 * the new field surfaces under Custom Fields / lead filters.
 */
export function useToggleMetaQuestion(campaignId?: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      formId: number;
      questionKey: string;
      enabled: boolean;
    }) => {
      const res = await axiosInstance.post(
        `${campaignFormsEndpoint}/${vars.formId}/questions/toggle`,
        {
          campaignId,
          questionKey: vars.questionKey,
          enabled: vars.enabled,
        }
      );
      return res.data as {
        enabled: boolean;
        customInputId: number | null;
        /** How many OTHER campaigns asking the same question were also enabled. */
        propagatedCampaigns?: number;
      };
    },
    onMutate: async (vars) => {
      const key = ["campaign", campaignId];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CampaignDetail>(key);
      if (previous) {
        queryClient.setQueryData<CampaignDetail>(key, {
          ...previous,
          formBreakdown: previous.formBreakdown.map((form) =>
            form.id !== vars.formId
              ? form
              : {
                  ...form,
                  questions: form.questions.map((q) =>
                    q.key === vars.questionKey
                      ? { ...q, enabled: vars.enabled }
                      : q
                  ),
                }
          ),
        });
      }
      return { previous };
    },
    onError: (error: any, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(["campaign", campaignId], ctx.previous);
      }
      toast.error(
        error?.response?.data?.message || "Failed to update question"
      );
    },
    onSuccess: (data) => {
      const extra = data.propagatedCampaigns ?? 0;
      toast.success(
        data.enabled
          ? extra > 0
            ? `Question turned on — also enabled on ${extra} other campaign${
                extra !== 1 ? "s" : ""
              } that ask${extra === 1 ? "s" : ""} it`
            : "Question turned on — answers will be captured as a custom field"
          : "Question turned off"
      );
    },
    onSettled: () => {
      // Broad invalidation: enabling propagates to sibling campaigns, so refetch
      // every campaign/form drawer, not just this one.
      queryClient.invalidateQueries({ queryKey: ["campaign"] });
      queryClient.invalidateQueries({ queryKey: ["campaign-form"] });
      invalidateCustomFieldCaches();
    },
  });
}

export function useCampaignForms() {
  const query = useQuery({
    queryKey: ["campaign-forms"],
    queryFn: async () => {
      const res = await axiosInstance.get(campaignFormsEndpoint);
      return res.data.forms as CampaignFormWithCounts[];
    },
  });

  return {
    forms: query.data,
    isLoading: query.isLoading,
  };
}

export type CampaignFormWithCounts = CampaignForm & {
  _count?: { leads: number; campaigns: number };
  /** Set for Web Forms (slug `web:…`) — the WEB_FORM source behind the form. */
  webForm?: { leadSourceId: number; publicKey: string } | null;
  /** Linked campaigns (via CampaignFormMapping) — for chips/subtitle. */
  campaigns?: Array<{
    campaign: { id: number; name: string; isActive: boolean };
  }>;
};

export type CampaignFormDetail = {
  id: number;
  name: string;
  slug: string;
  isActive: boolean;
  totalLeads: number;
  /** Distinct leads through this form that re-enquired at least once. */
  reenquiredLeads: number;
  campaignsCount: number;
  topSource: CampaignSource | null;
  /** The form's question snapshot WITHOUT switch state — shown when no
   *  campaign is linked yet (switch state lives in `campaignBreakdown`). */
  questions: CampaignFormQuestion[];
  /** Per-campaign split — questions are annotated PER campaign because the
   *  question→custom-field switch is campaign-scoped (same as the campaign
   *  drawer). */
  campaignBreakdown: Array<{
    id: number;
    name: string;
    isActive: boolean;
    totalLeads: number;
    sources: Array<CampaignSource & { count: number }>;
    questions: CampaignFormQuestion[];
  }>;
};

export function useCampaignFormDetail(id?: number | null) {
  const query = useQuery({
    queryKey: ["campaign-form", id],
    queryFn: async () => {
      const res = await axiosInstance.get(`${campaignFormsEndpoint}/${id}`);
      return res.data.form as CampaignFormDetail;
    },
    enabled: !!id,
  });

  return {
    form: query.data,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

/**
 * Same switch as `useToggleMetaQuestion`, driven from the FORM drawer: the
 * campaignId comes per-call (a form can be linked to several campaigns, each
 * with its own switch state). Optimistically flips the switch in the cached
 * form detail; invalidates the sibling campaign-detail cache and custom-field
 * caches so both drawers and the Custom Fields page stay in sync.
 */
export function useToggleFormQuestion(formId?: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      campaignId: number;
      questionKey: string;
      enabled: boolean;
    }) => {
      const res = await axiosInstance.post(
        `${campaignFormsEndpoint}/${formId}/questions/toggle`,
        {
          campaignId: vars.campaignId,
          questionKey: vars.questionKey,
          enabled: vars.enabled,
        }
      );
      return res.data as {
        enabled: boolean;
        customInputId: number | null;
        /** How many OTHER campaigns asking the same question were also enabled. */
        propagatedCampaigns?: number;
      };
    },
    onMutate: async (vars) => {
      const key = ["campaign-form", formId];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CampaignFormDetail>(key);
      if (previous) {
        queryClient.setQueryData<CampaignFormDetail>(key, {
          ...previous,
          campaignBreakdown: previous.campaignBreakdown.map((c) =>
            c.id !== vars.campaignId
              ? c
              : {
                  ...c,
                  questions: c.questions.map((q) =>
                    q.key === vars.questionKey
                      ? { ...q, enabled: vars.enabled }
                      : q
                  ),
                }
          ),
        });
      }
      return { previous };
    },
    onError: (error: any, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(["campaign-form", formId], ctx.previous);
      }
      toast.error(
        error?.response?.data?.message || "Failed to update question"
      );
    },
    onSuccess: (data) => {
      const extra = data.propagatedCampaigns ?? 0;
      toast.success(
        data.enabled
          ? extra > 0
            ? `Question turned on — also enabled on ${extra} other campaign${
                extra !== 1 ? "s" : ""
              } that ask${extra === 1 ? "s" : ""} it`
            : "Question turned on — answers will be captured as a custom field"
          : "Question turned off"
      );
    },
    onSettled: () => {
      // Broad invalidation: enabling propagates to sibling campaigns, so refetch
      // every campaign/form drawer, not just this one.
      queryClient.invalidateQueries({ queryKey: ["campaign-form"] });
      queryClient.invalidateQueries({ queryKey: ["campaign"] });
      invalidateCustomFieldCaches();
    },
  });
}

/** Web Forms are the CampaignForms whose slug is `web:<publicKey>`. */
export const isWebFormSlug = (slug: string | null | undefined) =>
  Boolean(slug?.startsWith("web:"));

/**
 * Flip a single form's ON/OFF switch. OFF blocks all new leads through that
 * form (the Meta sync skips it entirely); leads submitted while off are NOT
 * backfilled on re-enable. For a Web Form it is the "accepting responses"
 * switch (the public form checks it). It changes ONLY the form's own
 * `isActive` — never the source's on/off.
 */
export function useToggleCampaignForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { formId: number; isActive: boolean }) => {
      const res = await axiosInstance.patch(
        `${campaignFormsEndpoint}/${vars.formId}/toggle`,
        { isActive: vars.isActive }
      );
      return res.data.form as CampaignForm;
    },
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ["campaign-forms"] });
      const previous = queryClient.getQueryData<CampaignFormWithCounts[]>([
        "campaign-forms",
      ]);
      queryClient.setQueryData<CampaignFormWithCounts[]>(
        ["campaign-forms"],
        (old) =>
          old?.map((f) =>
            f.id === vars.formId ? { ...f, isActive: vars.isActive } : f
          )
      );
      return { previous };
    },
    onError: (error: any, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(["campaign-forms"], ctx.previous);
      }
      toast.error(error?.response?.data?.message || "Failed to update form");
    },
    onSuccess: (data, vars) => {
      const slug =
        data?.slug ??
        queryClient
          .getQueryData<CampaignFormWithCounts[]>(["campaign-forms"])
          ?.find((f) => f.id === vars.formId)?.slug;
      if (isWebFormSlug(slug)) {
        toast.success(
          vars.isActive
            ? "Form is accepting responses"
            : "Form turned off — it shows 'no longer accepting responses'"
        );
        return;
      }
      toast.success(
        vars.isActive
          ? "Form turned on — new leads will sync"
          : "Form turned off — leads from this form are blocked"
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-forms"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      // A Web Form's detail carries this switch ("accepting responses") —
      // refresh an open builder. The source's own on/off is untouched.
      queryClient.invalidateQueries({ queryKey: ["web-form"] });
    },
  });
}

/**
 * Master switch: turn every form ON or OFF in one call. Web Forms are left
 * alone (the server skips them) — each keeps its own switch.
 */
export function useToggleAllCampaignForms() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { isActive: boolean }) => {
      const res = await axiosInstance.patch(
        `${campaignFormsEndpoint}/toggle-all`,
        { isActive: vars.isActive }
      );
      return res.data as { count: number };
    },
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ["campaign-forms"] });
      const previous = queryClient.getQueryData<CampaignFormWithCounts[]>([
        "campaign-forms",
      ]);
      queryClient.setQueryData<CampaignFormWithCounts[]>(
        ["campaign-forms"],
        (old) =>
          old?.map((f) =>
            isWebFormSlug(f.slug) ? f : { ...f, isActive: vars.isActive }
          )
      );
      return { previous };
    },
    onError: (error: any, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(["campaign-forms"], ctx.previous);
      }
      toast.error(error?.response?.data?.message || "Failed to update forms");
    },
    onSuccess: (_data, vars) => {
      toast.success(
        vars.isActive ? "All forms turned on" : "All forms turned off"
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-forms"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
}
